import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import {
  Severity,
  hasClearanceViolation,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator
} from "../scenarioValidator";

export const shroudChecksNames = [
  "shroud_size_top_casing",
  "shroud_size_bottom_casing",
  "intake_size_shroud",
  "seal_size_shroud",
  "pump_size_shroud",
  "motor_size_shroud"
] as const;

export const shroudChecks: ValidationRuleGenerator = ({
  calcs,
  equipment,
  wellProperties,
  stack
}) => [
  {
    name: "shroud_size_top_casing",
    message: addStackPrefixToMessage(
      `Clearance Error: Shroud OD is larger than the top Casing Interior Diameter`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      equipment.value?.r_shroud?.outer_diameter_inches,
      wellProperties.value?.r_top_casing?.drift_inches
    ]),
    isInvalid:
      equipment.value?.r_shroud?.outer_diameter_inches >
      wellProperties.value?.r_top_casing?.drift_inches,
    disablesRules: ["motor_size_top_casing", "shroud_size_bottom_casing"],
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: "shroud_size_bottom_casing",
    message: addStackPrefixToMessage(
      `Clearance Error: Shroud OD is larger than the bottom Casing Interior Diameter`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      valuesExist([
        equipment.value?.r_shroud?.outer_diameter_inches,
        wellProperties.value?.r_bottom_casing?.drift_inches
      ]) &&
      calcs.equipmentLengths.bottomOfPumpsDepthFt >
        wellProperties.value?.top_casing_length_feet,
    isInvalid:
      equipment.value?.r_shroud?.outer_diameter_inches >
      wellProperties.value?.r_bottom_casing?.drift_inches,
    disablesRules: ["motor_size_bottom_casing"],
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS
  },
  {
    name: "pump_size_shroud",
    message: addStackPrefixToMessage(
      `Clearance Error: Shroud too small for pump`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      equipment.value?.r_shroud?.interior_diameter_inches,
      equipment.value?.r_mle?.depth_inches
    ]),
    isInvalid: calcs.ascendingOrderPumps.value.some(xref => {
      return hasClearanceViolation(
        equipment.value?.r_shroud?.interior_diameter_inches,
        xref.r_pump?.housing_outer_diameter_inches,
        wellProperties.value?.r_top_casing?.drift_inches,
        equipment.value?.r_mle?.depth_inches,
        true
      );
    }),
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
    disablesRules: [
      "pump_size_top_casing",
      "pump_size_bottom_casing",
      "intake_size_shroud",
      "seal_size_shroud",
      "motor_size_shroud"
    ]
  },
  {
    name: "intake_size_shroud",
    message: addStackPrefixToMessage(
      `Clearance Error: Shroud too small for intake`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      equipment.value?.r_shroud?.interior_diameter_inches,
      equipment.value?.r_mle?.depth_inches
    ]),
    isInvalid: calcs.orderedIntakes.value.some(xref => {
      return hasClearanceViolation(
        equipment.value?.r_shroud?.interior_diameter_inches,
        xref.r_intake?.outer_diameter_inches,
        wellProperties.value?.r_top_casing?.drift_inches,
        equipment.value?.r_mle?.depth_inches,
        true
      );
    }),
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
    disablesRules: [
      "intake_size_bottom_casing",
      "intake_size_top_casing",
      "seal_size_shroud",
      "motor_size_shroud"
    ]
  },
  {
    name: "seal_size_shroud",
    message: addStackPrefixToMessage(
      `Clearance Error: Shroud too small for seal`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      equipment.value?.r_shroud?.interior_diameter_inches,
      equipment.value?.r_mle?.depth_inches
    ]),
    isInvalid: calcs.descendingOrderSeals.value.some(xref => {
      return hasClearanceViolation(
        equipment.value?.r_shroud?.interior_diameter_inches,
        xref.r_seal?.outer_diameter_inches,
        wellProperties.value?.r_top_casing?.drift_inches,
        equipment.value?.r_mle?.depth_inches,
        true
      );
    }),
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
    disablesRules: [
      "seal_size_bottom_casing",
      "seal_size_top_casing",
      "motor_size_shroud"
    ]
  },
  {
    name: "motor_size_shroud",
    message: addStackPrefixToMessage(
      `Clearance Error: Shroud too small for motor`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      valuesExist([
        equipment.value?.r_shroud?.interior_diameter_inches,
        equipment.value?.r_motor?.outer_diameter_inches
      ]) && equipment.value?.r_shroud?.interior_diameter_inches > 0,
    isInvalid: hasClearanceViolation(
      equipment.value?.r_shroud?.interior_diameter_inches,
      equipment.value?.r_motor?.outer_diameter_inches,
      wellProperties.value?.r_top_casing?.drift_inches,
      0,
      true
    ),
    rulePlacement: EquipmentCalculatorSectionKey.INTAKE_CONDITIONS,
    disablesRules: ["motor_size_bottom_casing", "motor_size_top_casing"]
  }
];
